import React from "react";
import {
  Box,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";

/**
 * Media Viewer Dialog Component
 * 
 * This component displays a full-screen dialog for viewing media files (images/videos)
 * with navigation controls, download options, and responsive design.
 * 
 * Props:
 * - open: boolean - Controls whether the dialog is open
 * - onClose: function - Callback to close the dialog
 * - selectedMedia: object - Media data containing attachments array
 * - currentMediaIndex: number - Current media index being displayed
 * - onPrevMedia: function - Callback for previous media navigation
 * - onNextMedia: function - Callback for next media navigation
 * - onDownloadMedia: function - Callback for downloading current media
 * - onDownloadAllMedia: function - Callback for downloading all media
 */

// Styled components defined outside to prevent re-creation on every render
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "90vw",
    maxHeight: "90vh",
    backgroundColor: "transparent",
    boxShadow: "none",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100vw",
      maxHeight: "100vh",
      margin: 0,
      borderRadius: 0,
    },
  },
}));

const MediaViewerContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  backgroundColor: "rgba(0, 0, 0, 0.9)",
  color: "white",
}));

const MediaViewerImage = styled("img")({
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
});

const MediaViewerVideo = styled("video")({
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
});

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  color: "white",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
  zIndex: 1,
  [theme.breakpoints.down("sm")]: {
    width: 32,
    height: 32,
  },
}));

const MediaViewerDialog = ({
  open,
  onClose,
  selectedMedia,
  currentMediaIndex,
  onPrevMedia,
  onNextMedia,
  onDownloadMedia,
  onDownloadAllMedia,
}) => {
  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
    >
      <MediaViewerContent>
        {/* close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "white",
            zIndex: 2,
            padding: { xs: 0.5, sm: 1 },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
        </IconButton>

        {/* media counter */}
        {selectedMedia && (
          <Box
            sx={{
              position: "absolute",
              top: { xs: 8, sm: 16 },
              left: { xs: 8, sm: 16 },
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              padding: { xs: "2px 8px", sm: "4px 12px" },
              borderRadius: 2,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              fontWeight: "bold",
              zIndex: 2,
            }}
          >
            {currentMediaIndex + 1} / {selectedMedia.attachments.length}
          </Box>
        )}

        {/* navigation buttons */}
        {selectedMedia && currentMediaIndex > 0 && (
          <NavigationButton
            onClick={onPrevMedia}
            sx={{
              left: { xs: 8, sm: 16 },
              padding: { xs: 0.5, sm: 1 },
            }}
          >
            <PrevIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
          </NavigationButton>
        )}

        {selectedMedia &&
          currentMediaIndex < selectedMedia.attachments.length - 1 && (
            <NavigationButton
              onClick={onNextMedia}
              sx={{
                right: { xs: 8, sm: 16 },
                padding: { xs: 0.5, sm: 1 },
              }}
            >
              <NextIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
            </NavigationButton>
          )}

        {/* media content */}
        {selectedMedia &&
          selectedMedia.attachments[currentMediaIndex] && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {selectedMedia.attachments[currentMediaIndex].type === "image" ? (
                <MediaViewerImage
                  src={selectedMedia.attachments[currentMediaIndex].url}
                  alt={
                    selectedMedia.attachments[currentMediaIndex].fileName ||
                    "image"
                  }
                />
              ) : selectedMedia.attachments[currentMediaIndex].type ===
                "video" ? (
                <MediaViewerVideo
                  src={selectedMedia.attachments[currentMediaIndex].url}
                  controls
                  autoPlay
                  playsInline
                />
              ) : selectedMedia.attachments[currentMediaIndex].type === "document" ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    p: 4,
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: 4,
                    maxWidth: "80%",
                  }}
                >
                  <FileIcon sx={{ fontSize: 100, color: "white" }} />
                  <Typography variant="h6" sx={{ color: "white", textAlign: "center" }}>
                    {selectedMedia.attachments[currentMediaIndex].fileName || "document.pdf"}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    href={selectedMedia.attachments[currentMediaIndex].url}
                    target="_blank"
                    download
                    sx={{
                      bgcolor: "white",
                      color: "black",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.8)",
                      },
                    }}
                  >
                    download document
                  </Button>
                </Box>
              ) : null}
            </Box>
          )}

        {/* action buttons */}
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 8, sm: 16 },
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: { xs: 1, sm: 2 },
            zIndex: 2,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={onDownloadMedia}
            size={window.innerWidth < 600 ? "small" : "medium"}
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
              },
            }}
          >
            save current
          </Button>
          {selectedMedia && selectedMedia.attachments.length > 1 && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={onDownloadAllMedia}
              size={window.innerWidth < 600 ? "small" : "medium"}
              sx={{
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.9)",
                },
              }}
            >
              save all
            </Button>
          )}
        </Box>
      </MediaViewerContent>
    </StyledDialog>
  );
};

export default MediaViewerDialog;
